import { useServerNotifications, notificationTarget } from './useServerNotifications';
import { useShop } from '@/contexts/ShopContext';
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminDashboard } from '@/hooks/queries/useAdminDashboard';
import { useAdminQuotePendingCount } from '@/hooks/queries/useAdminQuoteRequests';
import { useAdminSupportOpenCount } from '@/hooks/queries/useAdminSupportTickets';
import { useAdminSync } from '@/hooks/queries/useAdminSync';
import { useAccountStatus } from '@/hooks/queries/useAccountStatus';
import { useDashboard } from '@/hooks/queries/useDashboard';
import { api } from '@/lib/api';
import type {
  HeaderNotificationItem,
  HeaderNotificationSeverity,
  HeaderMode,
} from '@/types/header';

interface NotificationBaseItem {
  id: string;
  title: string;
  description: string;
  severity: HeaderNotificationSeverity;
  href?: string;
  timestamp?: string;
  read?: boolean;
}

interface HeaderNotificationsResult {
  items: HeaderNotificationItem[];
  unreadCount: number;
  isLoading: boolean;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
}

function toSeverity(severity: 'warning' | 'error' | 'info'): HeaderNotificationSeverity {
  if (severity === 'error') return 'critical';
  if (severity === 'warning') return 'warning';
  return 'info';
}

function getReadStorageKey(mode: HeaderMode, userId: string | null): string {
  return `header_notifications_read:${mode}:${userId ?? 'anonymous'}`;
}

function useReadState(mode: HeaderMode): {
  readIds: Set<string>;
  markAsRead: (id: string) => void;
  markAllAsRead: (ids: string[]) => void;
} {
  const { user } = useAuth();
  const { currentShop } = useShop();
  const storageKey = getReadStorageKey(mode, `${user?.id ?? 'anonymous'}:${currentShop?.id ?? ''}`);
  const readStored = (key: string): Set<string> => {
    try {
      return new Set(JSON.parse(window.localStorage.getItem(key) ?? '[]') as string[]);
    } catch {
      return new Set();
    }
  };
  const [state, setState] = useState(() => ({ key: storageKey, ids: readStored(storageKey) }));
  const readIds = useMemo(
    () => (state.key === storageKey ? state.ids : readStored(storageKey)),
    [state, storageKey],
  );
  const markAllAsRead = useCallback(
    (ids: string[]) => {
      setState((previous) => {
        const next = new Set(previous.key === storageKey ? previous.ids : readStored(storageKey));
        ids.forEach((id) => next.add(id));
        try {
          window.localStorage.setItem(storageKey, JSON.stringify([...next]));
        } catch {
          /* non-blocking */
        }
        return { key: storageKey, ids: next };
      });
    },
    [storageKey],
  );
  const markAsRead = useCallback((id: string) => markAllAsRead([id]), [markAllAsRead]);

  return { readIds, markAsRead, markAllAsRead };
}

function attachReadState(
  rawItems: NotificationBaseItem[],
  readIds: Set<string>,
): HeaderNotificationItem[] {
  return rawItems
    .sort((a, b) => {
      const weight = (severity: HeaderNotificationSeverity): number => {
        if (severity === 'critical') return 3;
        if (severity === 'warning') return 2;
        if (severity === 'success') return 1;
        return 0;
      };

      const severityDiff = weight(b.severity) - weight(a.severity);
      if (severityDiff !== 0) return severityDiff;

      const timeA = a.timestamp ? Date.parse(a.timestamp) : 0;
      const timeB = b.timestamp ? Date.parse(b.timestamp) : 0;
      return timeB - timeA;
    })
    .map((item) => ({
      ...item,
      read: item.read ?? readIds.has(item.id),
    }));
}

export function useTenantHeaderNotifications(): HeaderNotificationsResult {
  const { t, i18n } = useTranslation('common');
  const { data, isLoading, isFetching } = useDashboard('7d');
  const accountStatus = useAccountStatus();
  const { readIds, markAsRead, markAllAsRead } = useReadState('app');
  const queryClient = useQueryClient();
  const serverNotifications = useServerNotifications();
  const markServerRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markAllServerRead = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language === 'en' ? 'en-US' : 'fr-FR', {
        maximumFractionDigits: 0,
      }),
    [i18n.language],
  );

  const rawItems = useMemo<NotificationBaseItem[]>(() => {
    if (!data) return [];

    const items: NotificationBaseItem[] = [];
    const nowIso = new Date().toISOString();

    if (data.alerts.out_of_stock_count > 0) {
      items.push({
        id: 'tenant-stock-out',
        title: t('header_notif_out_of_stock_title', { count: data.alerts.out_of_stock_count }),
        description: t('header_notif_out_of_stock_desc'),
        severity: 'critical',
        href: '/stock/alerts',
        timestamp: nowIso,
      });
    }

    if (data.alerts.low_stock_count > 0) {
      items.push({
        id: 'tenant-stock-low',
        title: t('header_notif_low_stock_title', { count: data.alerts.low_stock_count }),
        description: t('header_notif_low_stock_desc'),
        severity: 'warning',
        href: '/stock/alerts',
        timestamp: nowIso,
      });
    }

    if (data.alerts.debt_customers_count > 0) {
      items.push({
        id: 'tenant-customer-debt',
        title: t('header_notif_debt_title', { count: data.alerts.debt_customers_count }),
        description: t('header_notif_debt_desc', {
          amount: `${formatter.format(data.alerts.debt_amount_total)} FCFA`,
        }),
        severity: 'warning',
        href: '/customers',
        timestamp: nowIso,
      });
    }

    const recentSale = data.recent_sales[0];
    if (recentSale?.created_at) {
      items.push({
        id: `tenant-sale-${recentSale.id}`,
        title: t('header_notif_last_sale_title', { invoice: recentSale.invoice_number }),
        description: t('header_notif_last_sale_desc', {
          amount: `${formatter.format(recentSale.total)} FCFA`,
        }),
        severity: 'success',
        href: `/sales/${recentSale.id}`,
        timestamp: recentSale.created_at,
      });
    }

    for (const alert of accountStatus.data?.alerts ?? []) {
      items.push({
        id: `tenant-account-${alert.code}`,
        title: alert.title,
        description: alert.message,
        severity: alert.severity === 'blocking' ? 'critical' : alert.severity,
        href: alert.cta_url ?? '/subscription',
        timestamp: nowIso,
      });
    }

    if (items.length === 0) {
      items.push({
        id: 'tenant-healthy',
        title: t('header_notif_healthy_title'),
        description: t('header_notif_healthy_desc'),
        severity: 'success',
        href: '/dashboard',
        timestamp: nowIso,
      });
    }

    return items;
  }, [accountStatus.data?.alerts, data, formatter, t]);

  const serverItems = useMemo<NotificationBaseItem[]>(
    () =>
      (serverNotifications.data?.data ?? []).map((notification) => ({
        id: `server:${notification.id}`,
        title: notification.title,
        description: notification.body ?? '',
        severity:
          notification.type === 'delivery_daily' &&
          Number(notification.data?.overdue_count ?? 0) > 0
            ? 'warning'
            : 'info',
        href: notificationTarget(notification),
        timestamp: notification.created_at,
        read: notification.is_read,
      })),
    [serverNotifications.data?.data],
  );

  const items = useMemo(
    () => attachReadState([...serverItems, ...rawItems], readIds),
    [rawItems, readIds, serverItems],
  );

  const unreadCount = useMemo(() => items.filter((item) => !item.read).length, [items]);

  return {
    items,
    unreadCount,
    isLoading: isLoading || isFetching || accountStatus.isLoading || serverNotifications.isLoading,
    markAsRead: (id) => {
      if (id.startsWith('server:')) {
        markServerRead.mutate(id.slice(7));
        return;
      }
      markAsRead(id);
    },
    markAllAsRead: () => {
      markAllAsRead(items.filter((item) => !item.id.startsWith('server:')).map((item) => item.id));
      markAllServerRead.mutate();
    },
  };
}

export function useAdminHeaderNotifications(): HeaderNotificationsResult {
  const { t } = useTranslation('common');
  const adminDashboard = useAdminDashboard();
  const pendingQuotes = useAdminQuotePendingCount();
  const supportOpenCount = useAdminSupportOpenCount();
  const sync = useAdminSync();
  const { readIds, markAsRead, markAllAsRead } = useReadState('admin');

  const rawItems = useMemo<NotificationBaseItem[]>(() => {
    const items: NotificationBaseItem[] = [];
    const nowIso = new Date().toISOString();

    const quoteCount = pendingQuotes.data ?? 0;
    if (quoteCount > 0) {
      items.push({
        id: 'admin-pending-quotes',
        title: t('header_notif_pending_quotes_title', { count: quoteCount }),
        description: t('header_notif_pending_quotes_desc'),
        severity: 'warning',
        href: '/quotes?status=pending',
        timestamp: nowIso,
      });
    }

    const supportCount = supportOpenCount.data ?? 0;
    if (supportCount > 0) {
      items.push({
        id: 'admin-support-tickets',
        title: t('header_notif_support_tickets_title', { count: supportCount }),
        description: t('header_notif_support_tickets_desc'),
        severity: 'warning',
        href: '/support?status=open',
        timestamp: nowIso,
      });
    }

    const pendingSubscriptions = adminDashboard.data?.pending_subscriptions.length ?? 0;
    if (pendingSubscriptions > 0) {
      items.push({
        id: 'admin-pending-subscriptions',
        title: t('header_notif_pending_subscriptions_title', { count: pendingSubscriptions }),
        description: t('header_notif_pending_subscriptions_desc'),
        severity: 'info',
        href: '/subscriptions',
        timestamp: nowIso,
      });
    }

    const unresolvedConflicts = sync.data?.unresolved_conflicts ?? 0;
    if (unresolvedConflicts > 0) {
      items.push({
        id: 'admin-sync-conflicts',
        title: t('header_notif_sync_conflicts_title', { count: unresolvedConflicts }),
        description: t('header_notif_sync_conflicts_desc'),
        severity: 'critical',
        href: '/sync',
        timestamp: nowIso,
      });
    }

    const errorOperations = sync.data?.operations_by_status.error ?? 0;
    if (errorOperations > 0) {
      items.push({
        id: 'admin-sync-failed-operations',
        title: t('header_notif_sync_failed_title', { count: errorOperations }),
        description: t('header_notif_sync_failed_desc'),
        severity: 'warning',
        href: '/sync',
        timestamp: nowIso,
      });
    }

    for (const alert of adminDashboard.data?.system_alerts ?? []) {
      items.push({
        id: `admin-alert-${alert.type}`,
        title: t('header_notif_system_alert_title', { count: alert.count }),
        description: alert.message,
        severity: toSeverity(alert.severity),
        href: '/system/logs',
        timestamp: alert.timestamp,
      });
    }

    if (items.length === 0) {
      items.push({
        id: 'admin-healthy',
        title: t('header_notif_admin_healthy_title'),
        description: t('header_notif_admin_healthy_desc'),
        severity: 'success',
        href: '/admin',
        timestamp: nowIso,
      });
    }

    return items;
  }, [
    adminDashboard.data?.pending_subscriptions.length,
    adminDashboard.data?.system_alerts,
    pendingQuotes.data,
    supportOpenCount.data,
    sync.data?.operations_by_status.error,
    sync.data?.unresolved_conflicts,
    t,
  ]);

  const items = useMemo(() => attachReadState(rawItems, readIds), [rawItems, readIds]);
  const unreadCount = useMemo(() => items.filter((item) => !item.read).length, [items]);

  return {
    items,
    unreadCount,
    isLoading: adminDashboard.isLoading || pendingQuotes.isLoading || sync.isLoading,
    markAsRead,
    markAllAsRead: () => markAllAsRead(items.map((item) => item.id)),
  };
}
